import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, LogOut, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "@/components/PageShell";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { PostEditor } from "@/components/admin/PostEditor";
import { api, formatPostDate, getToken, setToken } from "@/lib/api";

export default function AdminPage() {
  const [user, setUser] = useState(null); // null = checking, false = signed out
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const loadPosts = async () => setPosts(await api("/api/posts/admin/all", { auth: true }));

  useEffect(() => {
    if (!getToken()) {
      setUser(false);
      return;
    }
    api("/api/auth/me", { auth: true })
      .then(setUser)
      .catch(() => {
        setToken(null);
        setUser(false);
      });
  }, []);

  useEffect(() => {
    if (user) loadPosts().catch((e) => toast.error(e.message));
  }, [user]);

  const signOut = async () => {
    await api("/api/auth/logout", { method: "POST", auth: true }).catch(() => {});
    setToken(null);
    setUser(false);
    setPosts([]);
  };

  const onSaved = (saved) => {
    toast.success(editing?.id ? "Post updated" : "Post published");
    setEditing(null);
    setPosts((list) => (list.some((p) => p.id === saved.id) ? list.map((p) => (p.id === saved.id ? saved : p)) : [saved, ...list]));
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await api(`/api/posts/${post.id}`, { method: "DELETE", auth: true });
      setPosts((list) => list.filter((p) => p.id !== post.id));
      if (editing?.id === post.id) setEditing(null);
      toast.success("Post deleted");
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (user === null) return <PageShell testId="admin-page"><p className="text-sm text-ash">Loading…</p></PageShell>;
  if (user === false) return <PageShell testId="admin-page"><AdminLogin onSuccess={setUser} /></PageShell>;

  return (
    <PageShell eyebrow="EDITOR" title="Daily Blog" titleItalic="& News." description={`Signed in as ${user.email}. Posts appear on /blog and on the home page instantly.`} testId="admin-page">
      <div className="mb-10 flex flex-wrap items-center gap-4">
        <Link to="/blog" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ember hover:text-emberHover" data-testid="admin-view-blog-link">
          <ExternalLink className="h-4 w-4" /> View public blog
        </Link>
        <button type="button" onClick={signOut} data-testid="admin-logout-button" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ash hover:text-cream">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      <div className="grid gap-10 lg:grid-cols-[1fr_380px]">
        <PostEditor post={editing} onSaved={onSaved} onCancel={() => setEditing(null)} />

        <aside>
          <h2 className="font-display text-xl font-bold text-cream">All posts <span className="text-ash">({posts.length})</span></h2>
          <ul className="mt-5 space-y-3" data-testid="admin-post-list">
            {posts.map((post) => (
              <li key={post.id} className={`rounded-md border p-4 ${editing?.id === post.id ? "border-ember/70 bg-card2" : "border-line bg-card2/60"}`} data-testid={`admin-post-${post.id}`}>
                <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-ember">
                  {post.category}
                  {!post.published && <span className="rounded-full border border-line px-2 py-0.5 text-ash">Draft</span>}
                </p>
                <p className="mt-1.5 font-display text-sm font-bold leading-snug text-cream">{post.title}</p>
                <p className="mt-1 text-xs text-ash">{formatPostDate(post.created_at, "en")}</p>
                <div className="mt-3 flex items-center gap-4">
                  <button type="button" onClick={() => setEditing(post)} className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-cream hover:text-ember" data-testid={`admin-edit-${post.id}`}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button type="button" onClick={() => remove(post)} className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ash hover:text-ember" data-testid={`admin-delete-${post.id}`}>
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                  {post.published && (
                    <Link to={`/blog/${post.slug}`} className="ml-auto text-xs text-ash hover:text-ember" data-testid={`admin-open-${post.id}`}>Open →</Link>
                  )}
                </div>
              </li>
            ))}
            {posts.length === 0 && <li className="text-sm text-ash">No posts yet — write your first one.</li>}
          </ul>
        </aside>
      </div>
    </PageShell>
  );
}
